import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { safeStorage } from "@/shared/lib/storage/safe-storage.ts";

/** Inclusive epoch-ms range. */
export interface CoverageInterval {
  from: number;
  to: number;
}

interface DeviceCoverage {
  /** Ranges a `/videos/around` response returned at least one clip for. */
  covered: CoverageInterval[];
  /** Ranges probed that came back with zero clips (and are old enough to trust). */
  empty: CoverageInterval[];
  /** Last write (ms) — the eviction key when too many devices accumulate. */
  updatedAt: number;
}

/** Cameras kept before the least-recently-used one is evicted. */
const MAX_DEVICES = 12;
/** Merged intervals kept per list, per device (oldest dropped first). */
const MAX_INTERVALS = 32;
/** Coverage older than this is dropped — footage rotates out of the backend too. */
const RETENTION_MS = 3 * 24 * 60 * 60 * 1000;
/**
 * A probe that finds nothing is only trustworthy once the range is old enough
 * that no further upload can land in it. Clips reach the backend with a lag, so
 * "empty" near the live edge means "not uploaded YET" — persisting that would
 * permanently suppress the fetch that eventually brings the footage in.
 */
const EMPTY_SETTLE_MS = 15 * 60 * 1000;

/** Merge `{from,to}` into a sorted, non-overlapping list (touching ranges join). */
const mergeInto = (list: CoverageInterval[], from: number, to: number): CoverageInterval[] => {
  const sorted = [...list, { from, to }].sort((a, b) => a.from - b.from);
  const out: CoverageInterval[] = [];
  for (const interval of sorted) {
    const last = out[out.length - 1];
    if (last && interval.from <= last.to) {
      last.to = Math.max(last.to, interval.to);
    } else {
      out.push({ ...interval });
    }
  }
  return out;
};

/** Remove `[from,to]` from every interval in `list`, splitting where it lands inside one. */
const subtractRange = (list: CoverageInterval[], from: number, to: number): CoverageInterval[] => {
  const out: CoverageInterval[] = [];
  for (const interval of list) {
    if (interval.to <= from || interval.from >= to) {
      out.push(interval);
      continue;
    }
    if (interval.from < from) out.push({ from: interval.from, to: from });
    if (interval.to > to) out.push({ from: to, to: interval.to });
  }
  return out;
};

/** True when a single merged interval fully contains `[from,to]`. */
const containsRange = (list: CoverageInterval[], from: number, to: number): boolean =>
  list.some((interval) => interval.from <= from && interval.to >= to);

/** The parts of `[from,to]` not already known to hold footage. */
const subtractCovered = (
  covered: CoverageInterval[],
  from: number,
  to: number,
): CoverageInterval[] => {
  let gaps: CoverageInterval[] = [{ from, to }];
  for (const interval of covered) {
    gaps = gaps.flatMap((gap) => subtractRange([gap], interval.from, interval.to));
  }
  return gaps;
};

/** Drop expired intervals, then cap the list — sorted ascending, so keep the newest. */
const prune = (list: CoverageInterval[], cutoff: number): CoverageInterval[] =>
  list.filter((interval) => interval.to >= cutoff).slice(-MAX_INTERVALS);

/** Evict least-recently-updated devices down to {@link MAX_DEVICES}. */
const capDevices = (devices: Record<string, DeviceCoverage>): Record<string, DeviceCoverage> => {
  const keys = Object.keys(devices);
  if (keys.length <= MAX_DEVICES) return devices;
  const keep = keys
    .sort((a, b) => devices[b].updatedAt - devices[a].updatedAt)
    .slice(0, MAX_DEVICES);
  const next: Record<string, DeviceCoverage> = {};
  for (const key of keep) next[key] = devices[key];
  return next;
};

const EMPTY_DEVICE: DeviceCoverage = { covered: [], empty: [], updatedAt: 0 };

interface VideoCoverageState {
  devices: Record<string, DeviceCoverage>;
  /** Record that footage was found spanning `[from,to]` (clip min start → max end). */
  recordCovered: (deviceMxid: string, from: number, to: number) => void;
  /** Record that `[from,to]` was probed and held no footage. Ignored near the live edge. */
  recordEmpty: (deviceMxid: string, from: number, to: number) => void;
  /** True when `[from,to]` sits entirely inside a recorded empty range — skip the probe. */
  isRangeKnownEmpty: (deviceMxid: string, from: number, to: number) => boolean;
  /**
   * Parts of `[from,to]` this client has NOT probed yet — neither known to hold
   * footage nor known to be empty.
   *
   * The distinction the availability strip cannot otherwise draw: a gap in the
   * video track means "no footage here" and an unprobed stretch means "nobody has
   * asked yet", and they look identical while only one of them is going to
   * change. It matters most exactly when the operator notices — returning to the
   * tab after minutes away, when the window has moved over time nothing has
   * fetched.
   *
   * Empty when the device has no coverage at all: a cold strip is a boot state,
   * not a gap, and painting the whole window as pending on every load is noise.
   */
  missingRanges: (deviceMxid: string, from: number, to: number) => CoverageInterval[];
}

/**
 * Per-camera record of which time ranges the client has already PROBED for video.
 *
 * Stores TIMESTAMPS ONLY. Video rows carry short-lived signed URLs with no
 * expiry field and nothing in the app re-signs them (a dead URL just stalls the
 * `<video>` silently), so rows must never be persisted — they are re-fetched on
 * every mount. Consequently positive coverage here never suppresses a fetch; its
 * only jobs are to keep a stale "empty" record from outliving footage that later
 * showed up, and to describe what has been seen. Only known-EMPTY ranges
 * suppress a request, which is what stops every fresh mount re-probing the same
 * footage-less window.
 */
export const useVideoCoverageStore = create<VideoCoverageState>()(
  persist(
    (set, get) => ({
      devices: {},

      recordCovered: (deviceMxid, from, to) => {
        if (!deviceMxid || !(to > from)) return;
        set((state) => {
          const device = state.devices[deviceMxid] ?? EMPTY_DEVICE;
          // Footage exists here after all — retract any overlapping "empty" claim.
          const empty = subtractRange(device.empty, from, to);
          if (containsRange(device.covered, from, to) && empty.length === device.empty.length) {
            return state;
          }
          const now = Date.now();
          const cutoff = now - RETENTION_MS;
          return {
            devices: capDevices({
              ...state.devices,
              [deviceMxid]: {
                covered: prune(mergeInto(device.covered, from, to), cutoff),
                empty: prune(empty, cutoff),
                updatedAt: now,
              },
            }),
          };
        });
      },

      recordEmpty: (deviceMxid, from, to) => {
        if (!deviceMxid || !(to > from)) return;
        // Only trust "nothing here" once the whole range is past the upload lag.
        if (to > Date.now() - EMPTY_SETTLE_MS) return;
        set((state) => {
          const device = state.devices[deviceMxid] ?? EMPTY_DEVICE;
          if (containsRange(device.empty, from, to)) return state;
          const now = Date.now();
          const cutoff = now - RETENTION_MS;
          // Never claim a range is empty where footage was previously found.
          let empty = device.empty;
          for (const gap of subtractCovered(device.covered, from, to)) {
            empty = mergeInto(empty, gap.from, gap.to);
          }
          if (empty === device.empty) return state;
          return {
            devices: capDevices({
              ...state.devices,
              [deviceMxid]: {
                covered: prune(device.covered, cutoff),
                empty: prune(empty, cutoff),
                updatedAt: now,
              },
            }),
          };
        });
      },

      missingRanges: (deviceMxid, from, to) => {
        const device = get().devices[deviceMxid];
        if (!device?.covered.length || !(to > from)) return [];
        // Unprobed = not covered, and not already proven empty. Subtracting the
        // empty ranges too keeps a camera that genuinely has no footage in a
        // stretch from showing a permanent "loading" band over it.
        let gaps = subtractCovered(device.covered, from, to);
        for (const known of device.empty) {
          gaps = gaps.flatMap((gap) => subtractRange([gap], known.from, known.to));
        }
        return gaps;
      },

      isRangeKnownEmpty: (deviceMxid, from, to) => {
        const device = get().devices[deviceMxid];
        if (!device) return false;
        return containsRange(device.empty, from, to);
      },
    }),
    {
      name: "maestro-video-coverage",
      storage: createJSONStorage(() => safeStorage),
    },
  ),
);
